import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useForm, Controller } from 'react-hook-form';

import { useStyles } from '../styles';
import {
  AgentProfileResponseDto,
  TaskFlowDto,
  useAgentProfileEditControllerAgentProfileEditForUsersMutation,
} from '@shared/services/apiService';
import { MarkdownEditor } from '@shared/components/MarkdownEditor';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import {
  TaskFlowPanel,
  TaskFlowPanelHandle,
  TaskFlowSaveState,
} from '@features/Admin/Agent/ui/TaskFlowCanvas';

interface EditAgentProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (profile?: AgentProfileResponseDto) => void;
  onProfileUpdated?: (profile: AgentProfileResponseDto) => void;
  profile: AgentProfileResponseDto | null;
}

interface FormData {
  name: string;
  prompt: string;
  firstMessage: string;
  firstMessageInterruptionsEnabled: boolean;
}

export const EditAgentProfileModal: React.FC<EditAgentProfileModalProps> = ({
  open,
  onClose,
  onSuccess,
  onProfileUpdated,
  profile,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { showError } = useErrorHandler();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tab, setTab] = useState(0);
  const taskFlowPanelRef = useRef<TaskFlowPanelHandle>(null);
  const [taskFlowSaveState, setTaskFlowSaveState] = useState<TaskFlowSaveState>({
    canSave: false,
    isSaving: false,
  });
  const initializedProfileIdRef = useRef<string | null>(null);
  const [openingLineExpanded, setOpeningLineExpanded] = useState(false);

  const [editAgentProfile] = useAgentProfileEditControllerAgentProfileEditForUsersMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      prompt: '',
      firstMessage: '',
      firstMessageInterruptionsEnabled: false,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) {
      initializedProfileIdRef.current = null;
      return;
    }
    if (!profile || initializedProfileIdRef.current === profile.id) return;
    initializedProfileIdRef.current = profile.id;
    setTab(0);
    setTaskFlowSaveState({ canSave: false, isSaving: false });
    setOpeningLineExpanded(profile.firstMessageInterruptionsEnabled ?? false);
    reset({
      name: profile.name || '',
      prompt: profile.prompt || '',
      firstMessage: profile.firstMessage ?? '',
      firstMessageInterruptionsEnabled: profile.firstMessageInterruptionsEnabled ?? false,
    });
  }, [open, profile, reset]);

  const onSubmit = async (data: FormData) => {
    if (!profile?.id) return;

    try {
      setIsSubmitting(true);
      const normalizedFirstMessage = data.firstMessage.trim();

      const updatedProfile = await editAgentProfile({
        profileId: profile.id,
        agentProfileEditRequestDto: {
          name: data.name,
          prompt: data.prompt,
          firstMessage: normalizedFirstMessage === '' ? null : normalizedFirstMessage,
          firstMessageInterruptionsEnabled: data.firstMessageInterruptionsEnabled,
        },
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('assistantsPage.success.title'),
          description:
            t('assistantsPage.success.updated', { name: data.name }) ||
            `Agent profile "${data.name}" updated successfully`,
        },
      });

      onSuccess(updatedProfile);
    } catch (error) {
      showError({
        title: t('assistantsPage.errors.updateTitle'),
        error,
        suggestion: t('assistantsPage.errors.updateSuggestion'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setTab(0);
    setTaskFlowSaveState({ canSave: false, isSaving: false });
    setOpeningLineExpanded(false);
    onClose();
  };

  if (!profile) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.modalPaper }}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '100%', md: '94vw' },
          maxWidth: 'none',
          height: tab === 1 ? 'auto' : { xs: '100%', md: '90vh' },
          maxHeight: tab === 1 ? 'none' : { xs: '100%', md: '90vh' },
          m: { xs: 0, md: 2 },
        },
      }}>
      <DialogTitle sx={{ flexShrink: 0 }}>{t('assistantsPage.modals.edit.title')}</DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} className={classes.modalForm}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ px: { xs: 1, sm: 3 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tab label={t('assistantsPage.modals.tabs.general', { defaultValue: 'General' })} />
          <Tab label={t('assistantsPage.modals.tabs.taskFlow', { defaultValue: 'Task Flow' })} />
        </Tabs>

        <DialogContent
          dividers
          sx={{
            p: 0,
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
          <Box
            sx={{
              display: tab === 0 ? 'block' : 'none',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: { xs: 2, sm: 3 },
            }}>
            <Box className={classes.formField}>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('assistantsPage.modals.validation.name') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('assistantsPage.modals.edit.nameLabel')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    variant="outlined"
                    className={classes.filledField}
                  />
                )}
              />
            </Box>

            <BorderedGrid sx={{ p: 0, overflow: 'hidden' }}>
              <Accordion
                disableGutters
                elevation={0}
                expanded={openingLineExpanded}
                onChange={(_, expanded) => setOpeningLineExpanded(expanded)}
                sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="edit-agent-profile-opening-line-content"
                  id="edit-agent-profile-opening-line-header"
                  sx={{ minHeight: 72, px: 2.5, '& .MuiAccordionSummary-content': { mr: 1.5 } }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%">
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('assistantsPage.modals.openingLine.label')}
                    </Typography>
                    <FormGroup>
                      <Controller
                        name="firstMessageInterruptionsEnabled"
                        control={control}
                        render={({ field: switchField }) => (
                          <FormControlLabel
                            sx={{ mr: 0 }}
                            onClick={(event) => event.stopPropagation()}
                            control={
                              <CustomSwitch
                                checked={!!switchField.value}
                                inputProps={{
                                  'aria-label': t(
                                    'assistantsPage.modals.firstMessageInterruptionsEnabled.label',
                                  ),
                                }}
                                onClick={(event: { stopPropagation: () => any }) =>
                                  event.stopPropagation()
                                }
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  const enabled = event.target.checked;

                                  switchField.onChange(enabled);
                                  setOpeningLineExpanded(enabled);
                                }}
                              />
                            }
                            label=""
                          />
                        )}
                      />
                    </FormGroup>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails
                  id="edit-agent-profile-opening-line-content"
                  sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <Controller
                    name="firstMessage"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        minRows={3}
                        placeholder={t('assistantsPage.modals.openingLine.placeholder')}
                        variant="outlined"
                        className={classes.filledField}
                      />
                    )}
                  />
                </AccordionDetails>
              </Accordion>
            </BorderedGrid>

            <Box className={classes.formField}>
              <Controller
                name="prompt"
                control={control}
                rules={{ required: t('assistantsPage.modals.validation.prompt') }}
                render={({ field }) => (
                  <MarkdownEditor
                    value={field.value}
                    onChange={field.onChange}
                    label={t('assistantsPage.modals.edit.promptLabel')}
                    error={!!errors.prompt}
                    helperText={errors.prompt?.message}
                    required
                    height={420}
                  />
                )}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: tab === 1 ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: { xs: 1, sm: 3 },
            }}>
            {tab === 1 && (
              <TaskFlowPanel
                ref={taskFlowPanelRef}
                initialTaskFlow={profile.taskFlow}
                readOnly={false}
                profileName={profile.name}
                onSaveStateChange={setTaskFlowSaveState}
                onSave={async (taskFlow: TaskFlowDto | null) => {
                  const updatedProfile = await editAgentProfile({
                    profileId: profile.id,
                    agentProfileEditRequestDto: { taskFlow },
                  }).unwrap();
                  onProfileUpdated?.(updatedProfile);
                  return updatedProfile.taskFlow;
                }}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions className={classes.modalActions} sx={{ flexShrink: 0 }}>
          <Button type="button" onClick={handleClose} disabled={isSubmitting}>
            {t('assistantsPage.modals.cancel')}
          </Button>
          {tab === 1 && (
            <Button
              type="button"
              variant="contained"
              disabled={!taskFlowPanelRef.current || !taskFlowSaveState.canSave}
              onClick={() => void taskFlowPanelRef.current?.save()}>
              {taskFlowSaveState.isSaving
                ? t('assistantsPage.modals.taskFlow.saving')
                : t('assistantsPage.modals.taskFlow.save')}
            </Button>
          )}
          {tab === 0 && (
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting
                ? t('assistantsPage.modals.updating')
                : t('assistantsPage.modals.update')}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};
